/**
 * Broadcast notices - the polled read behind the header banner.
 *
 * Polls rather than riding /auth/me on purpose: AuthProvider fires /auth/me
 * once on mount with a plain fetch (no React Query), so anything carried on it
 * would only appear after a page reload. A banner that cannot reach people
 * mid-session is useless for "Vera is deploying in 10 minutes".
 *
 * FAILS SOFT, deliberately and in three places: no retry, errors surface as an
 * empty list, and the component renders nothing on empty. The banner must
 * never be able to break the shell it lives in.
 *
 * DOES NOT KEEP THE SESSION ALIVE, and this is load-bearing. Being mounted in
 * AppShell, this poll fires every 60 seconds from every page for as long as a
 * tab is visible, which for a while silently made the BFF's 60-minute
 * inactivity timeout unreachable: express-session pushed the expiry forward on
 * every request, poll or not. Since the 8/25 rework NO request counts as
 * activity: only the presence beacon in AuthProvider advances the idle clock,
 * so a new always-on poll needs no registration anywhere (POLL_PATHS is
 * gone). Note the queryFn below is a raw fetch whose 401 becomes an empty
 * feed, so an expired session is noticed by whichever bffFetch call 401s
 * next, never by this poll.
 *
 * Design of record: docs/design/broadcast/PLAN.md
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bffFetch } from '../../../api/bffFetch';

export type BroadcastSeverity = 'info' | 'warning' | 'critical';

export interface BroadcastNotice {
  id: string;
  message: string;
  linkUrl: string | null;
  linkLabel: string | null;
  severity: BroadcastSeverity;
  dismissible: boolean;
  revision: number;
}

export interface BroadcastFeed {
  /** Live and not dismissed. The banner renders these. */
  notices: BroadcastNotice[];
  /**
   * Live, still in window, still addressed to this person, but dismissed.
   * The bell exists so these stay reachable: dismissing hides a message, it
   * does not lose it.
   */
  dismissed: BroadcastNotice[];
}

export const BROADCAST_QUERY_KEY = ['shell', 'notices'] as const;

/** Matches the Ideas notifications bell, the other polled header surface. */
const POLL_MS = 60_000;

const EMPTY_FEED: BroadcastFeed = { notices: [], dismissed: [] };

/**
 * ONE query for both header surfaces. The banner and the bell read the same
 * cache entry, so adding the bell added no polling and cannot show the two of
 * them disagreeing about what is live.
 */
export function useBroadcasts() {
  return useQuery({
    queryKey: BROADCAST_QUERY_KEY,
    staleTime: 30_000,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    // One failed poll must not produce a retry storm across every open tab.
    retry: false,
    queryFn: async (): Promise<BroadcastFeed> => {
      try {
        const res = await fetch('/api/shell/notices', { credentials: 'include' });
        if (!res.ok) return EMPTY_FEED;
        const body = (await res.json()) as Partial<BroadcastFeed>;
        return { notices: body.notices ?? [], dismissed: body.dismissed ?? [] };
      } catch {
        return EMPTY_FEED;
      }
    },
  });
}

export function useDismissBroadcast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await bffFetch(`/api/shell/notices/${encodeURIComponent(id)}/dismiss`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to dismiss');
    },
    // Optimistic: the strip goes on click, not on round trip. It MOVES into
    // the dismissed list rather than vanishing, so the bell picks it up in the
    // same frame the banner drops it.
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: BROADCAST_QUERY_KEY });
      const previous = queryClient.getQueryData<BroadcastFeed>(BROADCAST_QUERY_KEY);
      queryClient.setQueryData<BroadcastFeed>(BROADCAST_QUERY_KEY, (old) => {
        const feed = old ?? EMPTY_FEED;
        const hit = feed.notices.find((n) => n.id === id);
        if (!hit) return feed;
        return {
          notices: feed.notices.filter((n) => n.id !== id),
          dismissed: [...feed.dismissed, hit],
        };
      });
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(BROADCAST_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: BROADCAST_QUERY_KEY });
    },
  });
}
